<template lang="html">
  <div>
    <draggable
      v-model="imageArray"
      class="row"
      handle=".slidehandle"
      @update="onUpdate"
    >
      <div
        v-for="(image, idx) in imageArray"
        :key="'sg_thumbs_' + image.filename"
        v-b-tooltip.hover
        :title="image.name"
        :class="{ 'unused-image': idx >= trimmed_image_count }"
        class="col-xl-2 col-lg-2 col-md-3 col-sm-6 col-6 mt-3 slide-item unselectable"
      >
        <!-- This is the first time the image is loaded post-sizing. Give these more chances to load. -->
        <ReloadingImage
          v-if="image.type != 'video'"
          :src="
            'https://cdn3.soundslides.com/images/' +
              image.source_id +
              '/400/' +
              image.filename
          "
          fluid
          thumbnail
          :reload-limit="7"
          :max_width="160"
          :max_height="140"
          class="slidehandle"
        />
        <ReloadingImage
          v-if="image.type == 'video'"
          :src="
            'https://cdn3.soundslides.com/video/' +
              image.source_id +
              '/' +
              image.filename +
              '_00001.jpg'
          "
          fluid
          thumbnail
          :reload-limit="7"
          :max_width="160"
          :max_height="140"
          class="slidehandle"
        />
        <div v-if="image.type == 'video'" class="video-duration">
          <v-icon name="video" class="small" />
          {{ returnVideoDuration(image.filename) }}s
        </div>

        <div
          v-b-tooltip.hover.left
          title="Remove slide"
          class="remove-image"
          @click="removeImage(image)"
        >
          <v-icon name="times-circle" class="mt-1" />
        </div>

        <div v-if="!use_all_images" class="mt-1">
          <b-btn
            v-if="idx < trimmed_image_count"
            v-b-tooltip.hover
            title="Move to bottom"
            size="sm"
            variant="outline-danger"
            class="small"
            @click="moveToEndOfEdit(idx)"
          >
            <v-icon name="arrow-down" />
          </b-btn>
          <b-btn
            v-if="idx >= trimmed_image_count"
            v-b-tooltip.hover
            title="Move to top"
            size="sm"
            variant="outline-success"
            @click="moveIntoEdit(idx)"
          >
            <v-icon name="arrow-up" />
          </b-btn>
        </div>
      </div>
    </draggable>
    <!-- Removed images -->

    <div v-if="!viewRemoved && removedImages.length > 0" class="mt-5">
      <b-btn size="sm" variant="secondary" @click="viewRemoved = true">
        View {{ removedImages.length }} removed
        {{ "slide" | pluralize(removedImages.length) }}
      </b-btn>
    </div>

    <div v-if="viewRemoved && removedImages.length > 0" class="mt-5">
      <h3 class="mb-3">
        {{ removedImages.length }} removed
        {{ "slide" | pluralize(removedImages.length) }}
        <span class="ml-2">
          <b-btn
            size="sm"
            variant="outline-secondary"
            @click="viewRemoved = false"
          >
            Hide
          </b-btn>
        </span>
      </h3>

      <p
        v-if="$store.state.imageImportLimit - show_images.length <= 0"
        class="mt-3 mb-3 alert alert-warning"
      >
        Max images imported. Remove another image before restoring an image.
      </p>

      <p
        v-for="(image, idx) in removedImages"
        :key="'removed_' + image.name + idx"
        class="small"
      >
        <ReloadingImage
          v-if="image.type != 'video'"
          :src="
            'https://cdn3.soundslides.com/images/' +
              image.source_id +
              '/400/' +
              image.filename
          "
          fluid
          thumbnail
          :reload-limit="7"
          :max_height="80"
        />
        <ReloadingImage
          v-if="image.type == 'video'"
          :src="
            'https://cdn3.soundslides.com/video/' +
              image.source_id +
              '/' +
              image.filename +
              '_00001.jpg'
          "
          fluid
          thumbnail
          :reload-limit="7"
          :max_height="80"
        />

        <span class="ml-2">{{ image.name }}</span>
        <b-btn
          variant="light"
          size="sm"
          class="mini ml-2"
          :disabled="$store.state.imageImportLimit - show_images.length <= 0"
          @click="restoreImage(image)"
        >
          Restore
        </b-btn>
      </p>
    </div>
  </div>
</template>

<script>
const arrayMoveMutate = (array, from, to) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};
import find from "lodash/find";
import draggable from "vuedraggable";
import ReloadingImage from "@/components/ReloadingImage";
export default {
  name: "SortableGrid",
  filters: {
    pluralize(word, amount) {
      if (amount > 1) {
        return `${word}s`;
      }
      return word;
    }
  },
  components: {
    ReloadingImage,
    draggable
  },
  props: {
    passedArray: {
      type: Array,
      default: () => []
    },
    trimmed_image_count: {
      type: Number,
      default: () => 0
    },
    use_all_images: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      imageArray: [],
      viewRemoved: false
    };
  },
  computed: {
    removedImages() {
      return (
        (this.$store.state.show.currentShow || {}).staticImagesRemovedByUser ||
        []
      );
    },
    show_images() {
      if ((this.$store.state.show.currentShow || {}).data.images) {
        return (this.$store.state.show.currentShow || {}).data.images;
      }
      return [];
    }
  },
  watch: {
    passedArray(value) {
      this.imageArray = value;
    }
  },
  mounted() {
    this.imageArray = this.passedArray;
  },
  methods: {
    onUpdate() {
      // Emit out to parent.
      this.$emit("onUpdate", this.imageArray);
    },
    moveIntoEdit(idx) {
      console.log("moveIntoEdit", idx);
      var from = idx;
      var to = 0;
      arrayMoveMutate(this.imageArray, from, to);
      this.onUpdate();
    },
    moveToEndOfEdit(idx) {
      console.log("moveToEndOfEdit", idx);
      var from = idx;
      var to = -1;
      arrayMoveMutate(this.imageArray, from, to);
      this.onUpdate();
    },
    returnVideoDuration(filename) {
      var details = find(this.$store.state.show.deployedVideoFiles, {
        filename: filename
      });
      if (details) {
        return details.duration.toFixed(1);
      }
      return "";
    },
    removeImage(image) {
      console.log("removeImage", image);
      this.$store.dispatch("show/removeImage", image);
    },
    restoreImage(image) {
      console.log("restore", image);
      this.$store.dispatch("show/restoreImage", image);
    }
  }
};
</script>

<style lang="scss" scoped>
// Example usage:
@import "node_modules/bootstrap/scss/mixins";
@import "node_modules/bootstrap/scss/functions";
@import "node_modules/bootstrap/scss/variables";

@include media-breakpoint-up(sm) {
  .max-height-limit {
    max-height: 120px;
  }
}
.unused-image {
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
}
.video-duration {
  position: absolute;
  font-family: sans-serif;
  top: 5px;
  left: 20px;
  padding: 0px 4px 0px 4px;
  font-size: 0.8em;
  color: white;
  text-shadow: 2px 2px 10px black;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 0px 0px 4px 0px;
}
.slide-item:hover > .remove-image {
  visibility: visible;
}
.slide-item:hover > .video-duration {
  visibility: hidden;
}

.unselectable {
  -moz-user-select: -moz-none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -o-user-select: none;
  user-select: none;
}

.remove-image {
  visibility: hidden;
  position: absolute;
  font-family: sans-serif;
  top: 5px;
  left: 20px;
  padding: 0px 4px 0px 4px;
  font-size: 1em;
  color: white;
  text-shadow: 2px 2px 10px black;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 0px 0px 4px 0px;
}

.mini {
  font-size: 1em;
  padding: 0.5em;
}
</style>
